/**
 * Jobs Service
 * Handles all job-related API calls to the server
 * Manages cron jobs, scheduled tasks, and job monitoring
 */

import Parse from 'parse'
import { getServerBaseUrl } from '@/utils/serverUrl'

/** Mirrors server `ADMIN_CREATABLE_JOB_TYPES` — job templates admins may schedule. */
export const ADMIN_JOB_TYPES = [
  'health_check',
  'system_cleanup',
  'stripe_sync',
  'colyseus_room_stats',
  'payment_method_expiry_scan',
  'roadmap_vote_digest',
] as const

export type AdminJobType = (typeof ADMIN_JOB_TYPES)[number]

export interface Job {
  id: string
  name: string
  description: string
  schedule: string
  enabled: boolean
  lastRun?: string | null
  nextRun?: string | null
  status: 'idle' | 'running' | 'completed' | 'failed' | 'paused' | 'disabled'
  createdAt: string
  updatedAt: string
  metadata?: Record<string, any>
}

export interface JobExecution {
  id: string
  jobId?: string
  status: 'queued' | 'pending' | 'running' | 'completed' | 'failed' | 'timeout' | 'cancelled'
  startTime?: string
  endTime?: string
  duration?: number
  result?: any
  error?: string
  metadata?: Record<string, any>
}

export interface CreateJobPayload {
  name: string
  description: string
  schedule: string
  enabled?: boolean
  jobType: AdminJobType
}

export interface JobOverview {
  totalJobs: number
  enabledJobs: number
  disabledJobs: number
  runningJobs: number
  failedJobs: number
  completedJobs: number
  last24Hours: {
    totalExecutions: number
    successfulExecutions: number
    failedExecutions: number
  }
  systemHealth: {
    memoryUsage: any
    uptime: number
    nodeVersion: string
    platform: string
  }
}

class JobsService {
  private serverBaseUrl: string

  constructor() {
    this.serverBaseUrl = getServerBaseUrl()
  }

  /**
   * Get authentication headers for API requests
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    try {
      // Get current user session token
      const currentUser = Parse.User.current()
      if (currentUser) {
        const sessionToken = currentUser.getSessionToken()
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`
        }
      }
    } catch (error) {
      console.warn('Failed to get session token for jobs API:', error)
    }

    return headers
  }

  /**
   * Get all jobs
   */
  async getJobs(): Promise<{ success: boolean; jobs: Job[]; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          jobs: [], 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, jobs: data.jobs || [] }
    } catch (error: any) {
      console.error('Error fetching jobs:', error)
      return { success: false, jobs: [], error: error.message }
    }
  }

  /**
   * Get job overview/status
   */
  async getJobOverview(): Promise<{ success: boolean; overview: JobOverview; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/status/overview`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          overview: {
            totalJobs: 0,
            enabledJobs: 0,
            disabledJobs: 0,
            runningJobs: 0,
            failedJobs: 0,
            completedJobs: 0,
            last24Hours: {
              totalExecutions: 0,
              successfulExecutions: 0,
              failedExecutions: 0
            },
            systemHealth: {
              memoryUsage: null,
              uptime: 0,
              nodeVersion: 'unknown',
              platform: 'unknown'
            }
          }, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, overview: data.overview }
    } catch (error: any) {
      console.error('Error fetching job overview:', error)
      return { 
        success: false, 
        overview: {
          totalJobs: 0,
          enabledJobs: 0,
          disabledJobs: 0,
          runningJobs: 0,
          failedJobs: 0,
          completedJobs: 0,
          last24Hours: {
            totalExecutions: 0,
            successfulExecutions: 0,
            failedExecutions: 0
          },
          systemHealth: {
            memoryUsage: null,
            uptime: 0,
            nodeVersion: 'unknown',
            platform: 'unknown'
          }
        }, 
        error: error.message 
      }
    }
  }

  /**
   * Get a specific job by ID
   */
  async getJob(jobId: string): Promise<{ success: boolean; job?: Job; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/${jobId}`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, job: data.job }
    } catch (error: any) {
      console.error('Error fetching job:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create a new job (server requires `jobType` → metadata.type).
   */
  async createJob(jobData: CreateJobPayload): Promise<{ success: boolean; job?: Job; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs`, {
        method: 'POST',
        headers,
        body: JSON.stringify(jobData)
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, job: data.job }
    } catch (error: any) {
      console.error('Error creating job:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Update a job (Note: Currently only toggle is supported by the server)
   * For other updates, use the specific methods like toggleJob
   */
  async updateJob(jobId: string, updates: Partial<Job>): Promise<{ success: boolean; job?: Job; error?: string }> {
    // Currently the server only supports toggling enabled/disabled status
    if (updates.enabled !== undefined) {
      return this.toggleJob(jobId, updates.enabled)
    }
    
    return { success: false, error: 'Only enabled/disabled status can be updated. Use toggleJob method.' }
  }

  /**
   * Delete a job
   */
  async deleteJob(jobId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      return { success: true }
    } catch (error: any) {
      console.error('Error deleting job:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Run a job manually
   */
  async runJob(jobId: string): Promise<{
    success: boolean
    execution?: JobExecution
    message?: string
    error?: string
  }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/${jobId}/run`, {
        method: 'POST',
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return {
        success: true,
        execution: data.execution,
        message: data.message,
      }
    } catch (error: any) {
      console.error('Error running job:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get job execution history
   */
  async getJobHistory(jobId: string, limit: number = 50): Promise<{ success: boolean; executions: JobExecution[]; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/${jobId}/executions?limit=${limit}`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          executions: [], 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, executions: data.executions || [] }
    } catch (error: any) {
      console.error('Error fetching job history:', error)
      return { success: false, executions: [], error: error.message }
    }
  }

  /**
   * Toggle job enabled/disabled status
   */
  async toggleJob(jobId: string, enabled: boolean): Promise<{ success: boolean; job?: Job; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/${jobId}/toggle`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ enabled })
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { success: false, error: errorData.error || `HTTP ${response.status}: ${response.statusText}` }
      }

      const data = await response.json()
      return { success: true, job: data.job }
    } catch (error: any) {
      console.error('Error toggling job:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get system health status
   */
  async getSystemHealth(): Promise<{ success: boolean; health: any; error?: string }> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${this.serverBaseUrl}/api/jobs/health`, {
        headers
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { 
          success: false, 
          health: null, 
          error: errorData.error || `HTTP ${response.status}: ${response.statusText}` 
        }
      }

      const data = await response.json()
      return { success: true, health: data.health }
    } catch (error: any) {
      console.error('Error fetching system health:', error)
      return { success: false, health: null, error: error.message }
    }
  }
}

export const jobsService = new JobsService()
export default jobsService
